import React from "react";
import { CodeBlock } from "@ui/CodeBlock";

function DeveloperExpress() {
    return (
        <div className="max-w-3xl mx-auto py-10 px-4 sm:px-6 lg:py-12 lg:px-8">
            <h1 className="text-3xl font-bold text-gray-900  mb-8">
                Express.js Integration
            </h1>

            <p className="text-gray-600  mb-8">
                This guide demonstrates how to integrate Agent UI with an
                Express.js application, including automatic route discovery and
                registration.
            </p>

            <section className="mb-12">
                <h2 className="text-2xl font-bold text-gray-900  mb-4">
                    1. Setup and Configuration
                </h2>
                <p className="text-gray-600  mb-4">
                    First, initialize Express.js and the Agent UI SDK client:
                </p>
                <CodeBlock language="javascript">
                    {`import express from 'express';
import { SDKClient, FunctionType } from 'agent-ui-sdk';

const app = express();
const client = new SDKClient({
  apiKey: 'your-api-key'
});

// Middleware to parse JSON bodies
app.use(express.json());`}
                </CodeBlock>
            </section>

            <section className="mb-12">
                <h2 className="text-2xl font-bold text-gray-900  mb-4">
                    2. Define Express Routes
                </h2>
                <p className="text-gray-600  mb-4">
                    Create your Express routes with different types of
                    endpoints:
                </p>

                <div className="space-y-6">
                    <div>
                        <h3 className="text-lg font-semibold text-gray-900  mb-2">
                            Authentication Endpoint
                        </h3>
                        <CodeBlock language="javascript">
                            {`app.post('/auth', async (req, res) => {
  // Your authentication logic
  const { token } = req.body;
  // Validate token...
  res.json({ authenticated: true });
});`}
                        </CodeBlock>
                    </div>

                    <div>
                        <h3 className="text-lg font-semibold text-gray-900  mb-2">
                            Webhook Handler
                        </h3>
                        <CodeBlock language="javascript">
                            {`app.post('/webhooks/events', async (req, res) => {
  const { eventType, data } = req.body;
  // Process webhook event...
  res.json({ success: true });
});`}
                        </CodeBlock>
                    </div>

                    <div>
                        <h3 className="text-lg font-semibold text-gray-900  mb-2">
                            API Endpoints
                        </h3>
                        <CodeBlock language="javascript">
                            {`app.get('/api/users', async (req, res) => {
  // Get users logic...
  res.json({ users: [] });
});

app.post('/api/notifications', async (req, res) => {
  const { userId, message } = req.body;
  // Send notification logic...
  res.json({ sent: true });
});`}
                        </CodeBlock>
                    </div>
                </div>
            </section>

            <section className="mb-12">
                <h2 className="text-2xl font-bold text-gray-900  mb-4">
                    3. Route Discovery Function
                </h2>
                <p className="text-gray-600  mb-4">
                    Function to automatically discover all registered Express
                    routes:
                </p>
                <CodeBlock language="javascript">
                    {`async function registerAPIs() {
  try {
    // Discover all registered routes
    const routes = [];
    app._router.stack.forEach(function (middleware) {
      if (middleware.route) {
        // routes registered directly on the app
        routes.push({
          path: middleware.route.path,
          method: Object.keys(middleware.route.methods)[0].toUpperCase()
        });
      } else if (middleware.name === "router") {
        // router middleware
        middleware.handle.stack.forEach(function (handler) {
          if (handler.route) {
            routes.push({
              path: handler.route.path,
              method: Object.keys(handler.route.methods)[0].toUpperCase()
            });
          }
        });
      }
    });

    console.log('Discovered routes:', routes);`}
                </CodeBlock>
            </section>

            <section className="mb-12">
                <h2 className="text-2xl font-bold text-gray-900  mb-4">
                    4. Route Registration Configuration
                </h2>
                <p className="text-gray-600  mb-4">
                    Configure and register each route with appropriate settings:
                </p>
                <CodeBlock language="javascript">
                    {`    // Register each route with appropriate configuration
    await Promise.all(routes.map(route => {
      let config = {
        path: route.path,
        method: route.method,
        type: FunctionType.OUTGOING // default type
      };

      // Customize configuration based on route path
      if (route.path === '/auth') {
        config.type = FunctionType.AUTHENTICATION;
        config.parameters = { token: 'string' };
      } else if (route.path === '/webhooks/events') {
        config.type = FunctionType.INCOMING;
        config.parameters = {
          eventType: 'string',
          data: 'object'
        };
        config.requiresAuth = true;
      } else if (route.path === '/api/notifications') {
        config.parameters = {
          userId: 'string',
          message: 'string'
        };
        config.retryPolicy = {
          maxRetries: 3
        };
      }

      return client.registerFunction(config);
    }));`}
                </CodeBlock>
            </section>

            <section className="mb-12">
                <h2 className="text-2xl font-bold text-gray-900  mb-4">
                    5. Server Initialization
                </h2>
                <p className="text-gray-600  mb-4">
                    Start the server and register all APIs:
                </p>
                <CodeBlock language="javascript">
                    {`const PORT = process.env.PORT || 3000;
app.listen(PORT, async () => {
  console.log(\`Server running on port \${PORT}\`);
  await registerAPIs();
});`}
                </CodeBlock>
            </section>

            <section className="mb-12">
                <h2 className="text-2xl font-bold text-gray-900  mb-4">
                    Error Handling
                </h2>
                <p className="text-gray-600  mb-4">
                    The SDK throws descriptive errors that you can catch and
                    handle:
                </p>
                <CodeBlock language="typescript">
                    {`try {
  await client.registerFunction({
    path: '/api/function',
    method: 'POST'
  });
} catch (error) {
  if (error.response) {
    console.error(\`API Error: \${error.response.status}\`, error.response.data);
  } else {
    console.error('Network Error:', error.message);
  }
}`}
                </CodeBlock>
            </section>

            <section className="mb-12">
                <h2 className="text-2xl font-bold text-gray-900  mb-4">
                    Key Features
                </h2>
                <ul className="list-disc pl-6 space-y-2 text-gray-600 ">
                    <li>
                        <strong>Automatic Route Discovery:</strong> Scans your
                        Express.js application for all registered routes
                    </li>
                    <li>
                        <strong>Custom Configuration:</strong> Configures each
                        route with appropriate parameters and settings
                    </li>
                    <li>
                        <strong>Multiple Function Types:</strong> Supports
                        authentication, incoming webhooks, and outgoing API
                        calls
                    </li>
                    <li>
                        <strong>Error Handling:</strong> Includes comprehensive
                        error handling and logging
                    </li>
                </ul>
            </section>

            <section className="mb-12">
                <h2 className="text-2xl font-bold text-gray-900  mb-4">
                    Best Practices
                </h2>
                <ul className="list-disc pl-6 space-y-2 text-gray-600 ">
                    <li>
                        Always include proper error handling for route
                        registration
                    </li>
                    <li>
                        Configure appropriate retry policies for critical
                        endpoints
                    </li>
                    <li>
                        Use meaningful parameter descriptions for better AI
                        understanding
                    </li>
                    <li>
                        Implement proper authentication for sensitive endpoints
                    </li>
                </ul>
            </section>
        </div>
    );
}

export default DeveloperExpress;
